import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Subscription, timer } from 'rxjs';
import { FormBuilder, FormControl, FormGroup, NgForm } from '@angular/forms';
import {
	BlockFontSizeType,
	IPersonalSettings,
	NotificationsTypes,
	PlanogramEditTooltipField,
	PlanogramProductSvgType,
	ProductDepthType,
	ProductRotationPointType,
	SearchAlgorithmType
} from '../../models/personalSettings';
import { FilterItem } from '../filters-panel/models/filter';
import { MixpanelEvents } from '../../models/mixpanelEvents';
import { PanelHeaderTitle, SidePanelService } from '../../services/side-panel.service';
import { ServiceFeatures } from '../../models/serviceFeatures';
import { MixpanelService } from '../../services/mixpanel.service';
import { SessionService } from '../../services/session.service';
import { Utils } from '../../services/utils.service';
import { FiltersService } from '../filters-panel/filters.service';
import { SnackBarService } from '../../services/snack-bar.service';

import { SHARED_MODULES } from '../../../shared.';
import { PhIconComponent } from '../ph-icon/ph-icon.component';
import { PhLoaderComponent } from '../ph-loader/ph-loader.component';
import { TreeItemCheckboxComponent } from '../../../planogram/planogram-edit/components/add-products-panel/tree-item-checkbox/tree-item-checkbox.component';
import { HasFeaturePipe } from '../../pipes/has-feature.pipe';
import { HasAccessPipe } from '../../pipes/has-access.pipe';
import { RequiredFieldPipe } from '../../pipes/required-field.pipe';

type PersonalSettingsForm = {
	warningIndicators: FormControl<boolean>;
	errorIndicators: FormControl<boolean>;
	productDepthShelf: FormControl<ProductDepthType>;
	productDepthHook: FormControl<ProductDepthType>;
	productRotationPoint: FormControl<ProductRotationPointType>;
	planogramImagesMode: FormControl<boolean>;
	transparentProductImagesBackground: FormControl<boolean>;
	displayProductImageFrame: FormControl<boolean>;
	displayInactiveProductImageFrame: FormControl<boolean>;
	sequenceNumberMode: FormControl<boolean>;
	blockPercentsShow: FormControl<boolean>;
	planRulers: FormControl<boolean>;
	planGrid: FormControl<boolean>;
	planBackImage: FormControl<boolean>;
	packingOffset: FormControl<boolean>;
	transparentBoxesMode: FormControl<boolean>;
	dividePlanogramConfigurationImg: FormControl<boolean>;
	planogramProductSvg: FormControl<PlanogramProductSvgType>;
	blockNameWrap: FormControl<boolean>;
	blockFontSize: FormControl<BlockFontSizeType>;
	timeTrackerEnable: FormControl<boolean>;
	searchAlgorithm: FormControl<SearchAlgorithmType>;
    notifications: FormControl<Array<NotificationsTypes>>;
	notificationsWebDisabled: FormControl<Array<string>>;
	notificationsWebPushDisabled: FormControl<Array<string>>;
	planogramEditTooltipFields: FormControl<Array<PlanogramEditTooltipField>>;
}

type TreeItem = {
	id: string;
	name: string;
	isTranslate: boolean;
	checked?: boolean;
	opened?: boolean;
	checkedShadow?: boolean;
	has_children?: boolean;
	children?: Array<TreeItem>;
}

@Component({
    selector: 'app-personal-settings',
    templateUrl: './personal-settings.component.html',
    styleUrls: ['./personal-settings.component.scss'],
	imports: [
		SHARED_MODULES,
		HasAccessPipe,
		HasFeaturePipe,
		RequiredFieldPipe,
		PhIconComponent,
		PhLoaderComponent,
		TreeItemCheckboxComponent
	],
    standalone: true
})
export class PersonalSettingsComponent implements OnInit, OnDestroy {

	public formGroup: FormGroup<PersonalSettingsForm>;
	public isDisabledAnimation: boolean = true;
	public isManager: boolean = false;
    public notificationsTypesData: TreeItem;
    public notificationsTypesArray: Array<string> = Object.values(NotificationsTypes);
	public notificationsWebTypes: Array<FilterItem> = [];
	public showLoader: boolean = false;
	public subscriptions: Array<Subscription> = [];

	private _settings: IPersonalSettings;

	protected readonly features: typeof ServiceFeatures = ServiceFeatures;
	protected readonly planogramEditTooltipOptions: Array<{ field: PlanogramEditTooltipField; labelKey: string }> = [
		{ field: 'sequenceNumber', labelKey: 'COMMON.PLANOGRAM.SEQUENCE_NUMBER' },
		{ field: 'facesQty', labelKey: 'FACES_QTY' },
		{ field: 'totalPlanFacesQty', labelKey: 'TOTAL_PLAN_FACES_QTY' },
		{ field: 'recommendedFacesQty', labelKey: 'RECOMMENDED_FACES_QTY' },
		{ field: 'placementDepth', labelKey: 'PLACEMENT_DEPTH' },
		{ field: 'placementHeight', labelKey: 'PLACEMENT_HEIGHT' },
		{ field: 'price', labelKey: 'PRICE' },
		{ field: 'stockQty', labelKey: 'STOCK_QTY' }
	];

	@Output('apply') apply: EventEmitter<void> = new EventEmitter<void>();

	@ViewChild('form') formElement: NgForm;

	constructor(
		private formBuilder: FormBuilder,
		private mixpanelService: MixpanelService,
		private sessionService: SessionService,
		private sidePanelService: SidePanelService,
		private filtersService: FiltersService,
		private snackBarService: SnackBarService
	) {
		this.isManager = this.sessionService.defaults.isManager;
	}

	ngOnInit(): void {
		/* crutch for stopped mat expansion panel closed animation on init panel */
		timer(500).subscribe(() => this.isDisabledAnimation = false);

		this.sidePanelService.hasBackdrop = true;
		this.sidePanelService.mustSaveModal = true;
		this.sidePanelService.title = PanelHeaderTitle.PersonalSettings;

		if (this.sessionService.hasFeature(ServiceFeatures.Notifications)) {
			this.showLoader = true;
			this.filtersService.getNotificationsTypes().subscribe({
				next: (types: Array<FilterItem>) => {
					this.notificationsWebTypes = types;
					this.showLoader = false;
				},
				error: () => {
					this.snackBarService.setErrorNotification();
					this.showLoader = false;
				}
			});
		}

		this.setNotificationsTree();

		this.subscriptions.push(
			this.sidePanelService.applySubject.subscribe(() => {
				this.formElement.onSubmit(this.formGroup.value as any);
			})
		);

		this._settings = this.sessionService.personalSettings.settings;
		this.setFormGroup();
		this.mixpanelService.trackPage(MixpanelEvents.PersonalSettingsOpen);
	}

	applyPanel(): void {
		this.sidePanelService.disabledApply = this.formElement.submitted && this.formElement.invalid;
		if (this.formElement.invalid) return;
		this.showLoader = true;
		this.sidePanelService.disabledApply = true;
		this.sessionService.updatePersonalSettings(this.formGroup.value).subscribe(() => {
			this.showLoader = false;
			this.apply.emit();
			this.mixpanelService.trackPage(MixpanelEvents.PersonalSettingsApply, this.formGroup.value);
		});
	}

	onNotificationWebChange(value: 'enabled' | 'push', type: FilterItem): void {
		const controlsMap = {
			enabled: this.formGroup.controls.notificationsWebDisabled,
			push: this.formGroup.controls.notificationsWebPushDisabled,
		};

		const control = controlsMap[value] as FormControl<string[]>;
		const current: Array<string> = control.value ?? [];

		control.setValue(
			current.includes(type.id as string)
				? current.filter(id => id !== type.id)
				: [...current, type.id as string]
		);
	}

	togglePlanogramEditTooltipField(field: PlanogramEditTooltipField): void {
		const control: FormControl<Array<PlanogramEditTooltipField>> = this.formGroup.controls.planogramEditTooltipFields;
		const current: Array<PlanogramEditTooltipField> = control.value ?? [];
		control.setValue(
			current.includes(field)
				? current.filter((v: PlanogramEditTooltipField) => v !== field)
				: [...current, field]
		);
	}

	isPlanogramEditTooltipFieldChecked(field: PlanogramEditTooltipField): boolean {
		return (this.formGroup?.controls?.planogramEditTooltipFields?.value ?? []).includes(field);
	}

	onTreeCheck(item: TreeItem, tree: TreeItem, control: FormControl<Array<any>>, allIds: Array<any>): void {
		const onCheckControlValue = (element: TreeItem) => {
			if (!allIds.includes(element.id)) return;
			if (element.checked && !control.value.includes(element.id)) {
				const value: Array<any> = control.value;
				value.push(element.id);
				control.setValue(value);
			}
			if (!element.checked && control.value.includes(element.id)) {
				control.setValue(control.value.filter((el: any) => el !== element.id));
			}
		}

		item.checked = !item.checked;
		onCheckControlValue(item);
		if (item.children?.length) {
			Utils.rec(item.children, (child: TreeItem) => {
				child.checked = item.checked;
				onCheckControlValue(child);
			});
		}

		this.setTreeCheck(tree, control);
	}

	setNotificationsTree(): void {
		const emailChildren: Array<TreeItem> = [];
		const layoutChildren: Array<TreeItem> = [];
		if (this.isManager) {
			emailChildren.push(
				{
					id: NotificationsTypes.NewExecutionEmail,
					name: 'COMMON.PLANOGRAM.NEW',
					isTranslate: true
				}
			);
			layoutChildren.push(
				{
					id: NotificationsTypes.NewExecutionApp,
					name: 'COMMON.PLANOGRAM.NEW',
					isTranslate: true
				},
				{
					id: NotificationsTypes.NewTaskApp,
					name: 'CHAT.MENTION_IN_CHAT_YOU',
					isTranslate: true
				},
				{
					id: NotificationsTypes.DeadlineApp,
					name: 'COMMON.PLANOGRAM.OVERDUE_EXECUTION',
					isTranslate: true
				},
				{
					id: NotificationsTypes.ReturnedOnExecutionApp,
					name: 'COMMON.PLANOGRAM.RETURN_EXECUTION',
					isTranslate: true
				},
				{
					id: NotificationsTypes.CanceledExecutionApp,
					name: 'COMMON.PLANOGRAM.CANCEL_EXECUTION',
					isTranslate: true
				}
			)
		} else {
			emailChildren.push(
				{
					id: NotificationsTypes.ExecutionDoneEmail,
					name: 'COMMON.PLANOGRAM.EXECUTION',
					isTranslate: true
				}
			);
			layoutChildren.push(
				{
					id: NotificationsTypes.ExecutionDoneApp,
					name: 'COMMON.PLANOGRAM.EXECUTION',
					isTranslate: true
				},
				{
					id: NotificationsTypes.NewTaskApp,
					name: 'CHAT.MENTION_IN_CHAT_YOU',
					isTranslate: true
				},
				{
					id: NotificationsTypes.DeadlineApp,
					name: 'COMMON.PLANOGRAM.OVERDUE_EXECUTION',
					isTranslate: true
				}
			);
		}

		this.notificationsTypesData = {
			id: '-1',
			name: 'ALL',
			has_children: true,
			opened: true,
			isTranslate: true,
			children: [
				{
					id: '0',
					name: 'COMMON.EMAIL',
					opened: false,
					has_children: !!emailChildren.length,
					children: emailChildren,
					isTranslate: true
				},
				{
					id: '1',
					name: 'PlanoHero Layout',
					opened: false,
					has_children: !!layoutChildren.length,
					children: layoutChildren,
					isTranslate: false
				}
			]
		}
	}

	setTreeCheck(tree: TreeItem, control: FormControl<Array<any>>): void {
		Utils.rec(
			tree.children,
			(item: TreeItem) => {
				item.checked = control.value.includes(item.id)
					|| item.children?.every((item: TreeItem) => control.value.includes(item.id));
				item.checkedShadow = item.children?.length
					&& item.children.some((item: TreeItem) => control.value.includes(item.id))
					&& item.children.some((item: TreeItem) => !control.value.includes(item.id));
			}
		);
		tree.checked = tree.children.every((item: TreeItem) => item.checked);
		tree.checkedShadow = tree.children.some((item: TreeItem) => item.checked || item.checkedShadow)
			&& tree.children.some((item: TreeItem) => !item.checked);
	}

	setFormGroup(): void {
		this.formGroup = new FormGroup<PersonalSettingsForm>({
			warningIndicators: this.formBuilder.control<boolean>(this._settings.warningIndicators),
			errorIndicators: this.formBuilder.control<boolean>(this._settings.errorIndicators),
			productDepthShelf: this.formBuilder.control<ProductDepthType>(this._settings.productDepthShelf),
			productDepthHook: this.formBuilder.control<ProductDepthType>(this._settings.productDepthHook),
			planogramImagesMode: this.formBuilder.control<boolean>(this._settings.planogramImagesMode),
			transparentProductImagesBackground: this.formBuilder.control<boolean>(this._settings.transparentProductImagesBackground),
			displayProductImageFrame: this.formBuilder.control<boolean>(this._settings.displayProductImageFrame),
			displayInactiveProductImageFrame: this.formBuilder.control<boolean>(this._settings.displayInactiveProductImageFrame),
			sequenceNumberMode: this.formBuilder.control<boolean>(this._settings.sequenceNumberMode),
			blockPercentsShow: this.formBuilder.control<boolean>(this._settings.blockPercentsShow),
			planRulers: this.formBuilder.control<boolean>(this._settings.planRulers),
			planGrid: this.formBuilder.control<boolean>(this._settings.planGrid),
			planBackImage: this.formBuilder.control<boolean>(this._settings.planBackImage),
			packingOffset: this.formBuilder.control<boolean>(this._settings.packingOffset),
			transparentBoxesMode: this.formBuilder.control<boolean>(this._settings.transparentBoxesMode),
			dividePlanogramConfigurationImg: this.formBuilder.control<boolean>(
				this._settings.dividePlanogramConfigurationImg
			),
			planogramProductSvg: this.formBuilder.control<PlanogramProductSvgType>(this._settings.planogramProductSvg),
			blockNameWrap: this.formBuilder.control<boolean>(this._settings.blockNameWrap),
			blockFontSize: this.formBuilder.control<BlockFontSizeType>(this._settings.blockFontSize),
			timeTrackerEnable: this.formBuilder.control<boolean>(this._settings.timeTrackerEnable),
			searchAlgorithm: this.formBuilder.control<SearchAlgorithmType>(this._settings.searchAlgorithm),
			productRotationPoint: this.formBuilder.control<ProductRotationPointType>(this._settings.productRotationPoint),
            notifications: this.formBuilder.control<Array<NotificationsTypes>>([...this._settings.notifications]),
			notificationsWebDisabled: this.formBuilder.control<Array<string>>([...this._settings.notificationsWebDisabled]),
			notificationsWebPushDisabled: this.formBuilder.control<Array<string>>([...this._settings.notificationsWebPushDisabled]),
			planogramEditTooltipFields: this.formBuilder.control<Array<PlanogramEditTooltipField>>([...(this._settings.planogramEditTooltipFields ?? [])])
		});
		this.formGroup.valueChanges.subscribe(() => {
			this.sidePanelService.disabledApply = this.formElement?.submitted && this.formElement.invalid;
		});
		this.sidePanelService.disabledApply = true;
		this.setTreeCheck(this.notificationsTypesData, this.formGroup.controls.notifications);
	}

	ngOnDestroy(): void {
		this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
	}
}
